import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");

        if (!token) {
            toast.error("Invalid or missing password reset token.");
            return;
        }

        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        if (password.length < 8) {
            toast.error("Password must be at least 8 characters long.");
            return;
        }

        setLoading(true);

        try {
            const apiUrl = "https://consult.conversationaily.ai";

            const response = await axios.put(
                `${apiUrl}/api/users/reset-password`,
                {
                    token,
                    password,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                response.data?.message || "Password reset successfully!"
            );

            setPassword("");
            setConfirmPassword("");

            setTimeout(() => {
                navigate("/");
            }, 2000);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                "Unable to reset password. The link may have expired."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
                <div className="text-center">
                    <img
                        src="./logo.png"
                        alt="Company Logo"
                        className="mx-auto mb-6 h-20 w-52 object-contain"
                    />

                    <h2 className="text-2xl font-bold text-gray-800">
                        Reset Password
                    </h2>

                    <p className="mt-2 text-sm text-gray-500">
                        Enter your new password below.
                    </p>
                </div>

                {!token ? (
                    <div className="mt-6 rounded-lg bg-red-50 p-4 text-center text-sm text-red-600">
                        Invalid or missing password reset token.
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="mt-8">
                        <div>
                            <label
                                htmlFor="password"
                                className="mb-2 block text-sm font-medium text-gray-700"
                            >
                                New Password
                            </label>

                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    required
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        // Eye off
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M3 3l18 18" />
                                            <path d="M10.58 10.58a2 2 0 102.83 2.83" />
                                            <path d="M9.88 5.09A9.77 9.77 0 0112 5c5 0 8.27 5 9 7a13.16 13.16 0 01-1.64 2.85" />
                                            <path d="M6.61 6.61C4.62 7.95 3.35 9.82 3 12c.73 2 4 7 9 7a9.77 9.77 0 004.12-.91" />
                                        </svg>
                                    ) : (
                                        // Eye
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M2.06 12.35a1 1 0 010-.7C3.67 7.7 7.53 5 12 5s8.33 2.7 9.94 6.65a1 1 0 010 .7C20.33 16.3 16.47 19 12 19s-8.33-2.7-9.94-6.65z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="mt-4">
                            <label
                                htmlFor="confirmPassword"
                                className="mb-2 block text-sm font-medium text-gray-700"
                            >
                                Confirm Password
                            </label>

                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    required
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label={
                                        showConfirmPassword
                                            ? "Hide confirm password"
                                            : "Show confirm password"
                                    }
                                >
                                    {showConfirmPassword ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M3 3l18 18" />
                                            <path d="M10.58 10.58a2 2 0 102.83 2.83" />
                                            <path d="M9.88 5.09A9.77 9.77 0 0112 5c5 0 8.27 5 9 7a13.16 13.16 0 01-1.64 2.85" />
                                            <path d="M6.61 6.61C4.62 7.95 3.35 9.82 3 12c.73 2 4 7 9 7a9.77 9.77 0 004.12-.91" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M2.06 12.35a1 1 0 010-.7C3.67 7.7 7.53 5 12 5s8.33 2.7 9.94 6.65a1 1 0 010 .7C20.33 16.3 16.47 19 12 19s-8.33-2.7-9.94-6.65z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <p className="mt-2 text-xs text-gray-500">
                            Password must be at least 8 characters long.
                        </p>

                        {error && (
                            <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        {message && (
                            <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-600">
                                {message}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-6 w-full rounded-lg bg-orange-400 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? "Resetting..." : "Reset Password"}
                        </button>

                        <div className="mt-6 text-center">
                            <Link
                                to="/"
                                className="text-sm font-medium text-blue-600 hover:underline"
                            >
                                ← Back to Login
                            </Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;